import { ReactElement } from 'react'
import ClearIcon from '@mui/icons-material/Clear'
import { Box, Chip, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import useSceneElementDebugStore from './store'

const USER_DATA_MAX_CHARS = 4000

const formatNumber = (value: number): string => value.toFixed(3)

// Pretty-print the full userData. Functions are marked, circular references are
// broken (userData often holds Three object refs and handler callbacks), and the
// result is capped so a huge object can't blow up the box.
const stringifyUserData = (userData: Record<string, unknown>): string => {
  const seen = new WeakSet<object>()
  let text: string
  try {
    text =
      JSON.stringify(
        userData,
        (_key, value) => {
          if (typeof value === 'function') return '[fn]'
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) return '[circular]'
            seen.add(value)
          }
          return value
        },
        2,
      ) ?? 'undefined'
  } catch {
    text = '[unserializable]'
  }
  if (text.length > USER_DATA_MAX_CHARS) text = `${text.slice(0, USER_DATA_MAX_CHARS)}…`
  return text
}

/**
 * Lower-right overlay for the "Scene Element Debug" dev tool. Lists every object
 * under the last click, top-most first, with its name, the material's
 * visibility/opacity (so invisible interceptors stand out) and its userData
 * ("options"). Rendered above all other content.
 */
const SceneElementDebugBox = (): ReactElement => {
  const clickPoint = useSceneElementDebugStore(state => state.clickPoint)
  const hits = useSceneElementDebugStore(state => state.hits)
  const clear = useSceneElementDebugStore(state => state.clear)

  return (
    <Paper
      elevation={8}
      data-cy="scene-element-debug-box"
      sx={{
        position: 'absolute',
        right: 12,
        bottom: 12,
        zIndex: 2000,
        width: 360,
        maxHeight: '60%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Scene Element Debug
        </Typography>
        <Chip size="small" label={`${hits.length} hit${hits.length === 1 ? '' : 's'}`} />
        <Tooltip title="Auswahl löschen">
          <span>
            <IconButton size="small" onClick={clear} disabled={hits.length === 0}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Divider />

      <Box sx={{ overflow: 'auto', px: 1.5, py: 1 }}>
        {hits.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Klicke ein Element in der Szene an, um zu sehen, was unter dem Cursor liegt (oberstes
            zuerst).
          </Typography>
        ) : (
          <Stack spacing={1}>
            {clickPoint && (
              <Typography variant="caption" color="text.secondary">
                Trefferpunkt: {clickPoint.map(formatNumber).join(', ')}
              </Typography>
            )}

            {hits.map((hit, index) => {
              const userDataText = stringifyUserData(hit.userData)
              const isTop = index === 0
              return (
                <Box
                  key={`${hit.uuid}-${index}`}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: 1,
                    borderColor: isTop ? 'primary.main' : 'divider',
                    bgcolor: isTop ? 'action.selected' : 'transparent',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="body2" sx={{ fontWeight: 700, flexGrow: 1 }}>
                      {index + 1}. {hit.label}
                    </Typography>
                    {isTop && <Chip size="small" color="primary" label="oben" />}
                  </Stack>

                  <Typography variant="caption" color="text.secondary" display="block">
                    {hit.type}
                    {hit.name ? ` · name: ${hit.name}` : ''} · uuid {hit.uuid}
                  </Typography>

                  <Typography variant="caption" color="text.secondary" display="block">
                    dist {formatNumber(hit.distance)} · {hit.visible ? 'sichtbar' : 'verborgen'}
                    {hit.opacity !== null ? ` · opacity ${hit.opacity}` : ''}
                    {hit.transparent ? ' · transparent' : ''}
                  </Typography>

                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      userData
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        mt: 0.25,
                        p: 0.75,
                        borderRadius: 0.5,
                        bgcolor: 'action.hover',
                        fontFamily: 'monospace',
                        fontSize: 11,
                        lineHeight: 1.4,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        maxHeight: 180,
                        overflow: 'auto',
                      }}
                    >
                      {userDataText}
                    </Box>
                  </Box>
                </Box>
              )
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  )
}

export default SceneElementDebugBox
